import { forwardRef, type CSSProperties, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";

type StackGap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;

interface StackOwnerState {
  direction: "row" | "column";
  gap: StackGap;
  wrap: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities
 * (flex → direction → wrap → gap), verified against the frozen baseline.
 * align/justify keep their original inline-style treatment. */

const stackGapStyles: Record<StackGap, CSSObject> = {
  0: { gap: "0px" },
  1: { gap: ".25rem" },
  2: { gap: ".5rem" },
  3: { gap: ".75rem" },
  4: { gap: "1rem" },
  5: { gap: "1.25rem" },
  6: { gap: "1.5rem" },
  8: { gap: "2rem" },
  10: { gap: "2.5rem" },
  12: { gap: "3rem" }
};

const StackRoot = styled("div", {
  label: "AgenticStack-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: StackOwnerState }>(({ ownerState }) => [
  { display: "flex" },
  ownerState.direction === "row" ? { flexDirection: "row" } : { flexDirection: "column" },
  ownerState.wrap && { flexWrap: "wrap" },
  stackGapStyles[ownerState.gap]
]);

export type StackProps = HTMLAttributes<HTMLDivElement> & {
  direction?: "row" | "column";
  gap?: StackGap;
  align?: CSSProperties["alignItems"];
  justify?: CSSProperties["justifyContent"];
  wrap?: boolean;
};

export const Stack = forwardRef<HTMLDivElement, StackProps>(
  ({ className, direction = "column", gap = 4, align, justify, wrap, style, ...props }, ref) => (
    <StackRoot
      ref={ref}
      ownerState={{ direction, gap, wrap: Boolean(wrap) }}
      className={className}
      style={{ alignItems: align, justifyContent: justify, ...style }}
      {...props}
    />
  )
);

Stack.displayName = "Stack";
